import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { format } from 'date-fns';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { Color } from '../../../../core/enums/Color';
import { RootState } from '../../../../core/state/state';
import Modal from '../../../components/modal/Modal';
import Button from '../../../../components/form/button/Button';
import CheckBox from '../../../../components/form/checkbox/CheckBox';
import Input from '../../../../components/form/input/Input';
import { formatDate, getTimeDate } from '../../../../core/utils/date';
import { DATE_FORMAT_DATEPICKER_SHORT, DATE_FORMAT_ISO, TIME_FORMAT_BACKEND } from '../../../../core/constants/date';
import Radio from '../../../../components/form/radio/Radio';
import Badge from '../../../components/badge/Badge';
import deliveryZoneColorMap from '../../../../core/constants/deliveryZoneColorMap';
import RangeTimePicker from '../../../../components/form/range-time-picker/RangeTimePicker';
import { blockBookingSlot } from '../../../../core/state/bookings/asyncActions';
import { spawnGlobalNotification } from '../../../../core/state/global-notifications/asyncActions';
import { GlobalNotificationTypes } from '../../../../core/enums/GlobalNotificationTypes';
import { NotificationPosition } from '../../../../core/state/global-notifications/types';
import { generateEntityFetchListAsyncAction } from '../../../../core/state/entity/generateEntityAsyncActions';
import EntityName from '../../../../core/enums/EntityName';
import getStatus from '../../../../core/utils/getStatus';
import TextArea from '../../../../components/form/text-area/TextArea';
import { NEW_LINE_REGEX } from '../../../../core/constants/regex';

import './BlockBookingSlotModal.scss';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  dateFrom: Date;
  handleBookingModal: () => void;
}
interface FormData {
  dateFrom: string;
  dateTo: string;
  startTime: string;
  endTime: string;
  timeOptions: number;
  comment: string;
  allDeliveryZones: number;
  deliveryZone1: boolean;
  deliveryZone2: boolean;
  deliveryZone3: boolean;
  deliveryZone4: boolean;
  deliveryZone5: boolean;
  deliveryZone6: boolean;
  deliveryZone7: boolean;
  deliveryZone8: boolean;
  deliveryZone9: boolean;
  deliveryZone10: boolean;
  deliveryZone11: boolean;
  deliveryZone12: boolean;
  deliveryZone13: boolean;
  deliveryZone14: boolean;
  deliveryZone15: boolean;
  statusOpen: boolean;
  statusPending: boolean;
  statusDone: boolean;
  statusCanceled: boolean;
  recurringDays: [];
  recurringOptions: boolean;
  day0: boolean;
  day1: boolean;
  day2: boolean;
  day3: boolean;
  day4: boolean;
  day5: boolean;
  day6: boolean;
}

interface ExtendedFormData extends FormData {
  deliveryZoneTest: string;
  customCheckboxTest: string;
  pleaseSelectTime: string;
}

const today = new Date();
today.setHours(0, 0, 0, 0);

const BlockBookingSlotModal: React.FC<Props> = ({ isOpen, onClose, dateFrom, handleBookingModal }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const deliveryZones = useSelector((state: RootState) => state.zones.deliveryZones)?.filter((item) => item.active);
  const { operatingHoursTo, operatingHoursFrom } = useSelector((state: RootState) => state.zones);
  const fetchBlockedSlots = React.useMemo(() => generateEntityFetchListAsyncAction(EntityName.BlockedSlot), []);
  const { projectId } = useParams<{ projectId: string }>();

  let validationSchema = yup
    .object()
    .shape({
      comment: yup.string().max(100, t('projectSettings.blockBookingSlots.validation.comment')).nullable(),
      dateFrom: yup
        .date()
        .min(today, t('projectSettings.blockBookingSlots.form.option.dateIsInPast'))
        .max(yup.ref('dateTo'), t('validation.date.isAfter'))
        .typeError(t('projectSettings.blockBookingSlots.form.option.vaildDate')),
      dateTo: yup
        .date()
        .min(yup.ref('dateFrom'), t('projectSettings.blockBookingSlots.form.option.dateIsInPast'))
        .typeError(t('projectSettings.blockBookingSlots.form.option.vaildDate'))
    })
    .required();

  validationSchema = validationSchema.test('deliveryZoneTest', null, (obj: ExtendedFormData) => {
    if (
      Number(obj.allDeliveryZones) === 0 ||
      (Number(obj.allDeliveryZones) === 1 && !getStatus(obj as unknown as Record<string, unknown>, 'deliveryZone'))
    ) {
      return true;
    }
    return new yup.ValidationError(t('projectSettings.blockBookingSlots.form.option.checkbox'), null, 'deliveryZoneTest');
  });

  validationSchema = validationSchema.test('customCheckboxTest', null, (obj: ExtendedFormData) => {
    if (obj.recurringOptions === false || (obj.recurringOptions === true && !getStatus(obj as unknown as Record<string, unknown>, 'day'))) {
      return true;
    }
    return new yup.ValidationError(t('projectSettings.blockBookingSlots.form.option.checkbox'), null, 'customCheckboxTest');
  });

  validationSchema = validationSchema.test('pleaseSelectTime', null, (obj: ExtendedFormData) => {
    if (Number(obj.timeOptions) === 0 || (Number(obj.timeOptions) === 1 && obj.startTime !== '00:00:00' && obj.endTime !== '00:00:00')) {
      return true;
    }
    return new yup.ValidationError(t('projectSettings.blockBookingSlots.form.option.time'), null, 'pleaseSelectTime');
  });

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    setError,
    formState: { errors, isValid },
    clearErrors
  } = useForm<ExtendedFormData>({
    mode: 'all',
    defaultValues: {
      dateFrom: formatDate(dateFrom, DATE_FORMAT_ISO),
      dateTo: formatDate(dateFrom, DATE_FORMAT_ISO),
      startTime: '00:00:00',
      endTime: '00:00:00',
      timeOptions: 0,
      allDeliveryZones: 0,
      comment: '',
      deliveryZone1: false,
      deliveryZone2: false,
      deliveryZone3: false,
      deliveryZone4: false,
      deliveryZone5: false,
      deliveryZone6: false,
      deliveryZone7: false,
      deliveryZone8: false,
      deliveryZone9: false,
      deliveryZone10: false,
      deliveryZone11: false,
      deliveryZone12: false,
      deliveryZone13: false,
      deliveryZone14: false,
      deliveryZone15: false,
      recurringOptions: false,
      recurringDays: [],
      day0: false,
      day1: false,
      day2: false,
      day3: false,
      day4: false,
      day5: false,
      day6: false
    },
    resolver: yupResolver(validationSchema)
  });

  const closeModalDialog = () => {
    dispatch(
      spawnGlobalNotification({
        type: GlobalNotificationTypes.Info,
        postition: NotificationPosition.Top,
        title: t('common.notification.discard'),
        message: t('modal.blockBookingSlotModal.form.message.cancel')
      })
    );
    onClose();
  };

  const onSubmit = useCallback(
    async (formData: FormData) => {
      // Get selected zones from FormData object
      const selectedZones = Object.entries(formData)
        .filter(([key, value]) => key.startsWith('deliveryZone') && value === true)
        .map(([key]) => Number(key.replace('deliveryZone', '')))
        .filter((zone) => Number.isInteger(zone) && zone > 0);

      const selectedZonesIds = selectedZones?.map((zone) => deliveryZones?.find((i) => i.order === zone)?.id).toString();

      const selectedDays = [];

      selectedDays.push(formData.day0);
      selectedDays.push(formData.day1);
      selectedDays.push(formData.day2);
      selectedDays.push(formData.day3);
      selectedDays.push(formData.day4);
      selectedDays.push(formData.day5);
      selectedDays.push(formData.day6);

      const isRecurringDaysSelected = selectedDays.every((i) => i === false) ? [] : selectedDays;

      const data = {
        zoneIds: Number(formData.allDeliveryZones) === 0 ? '' : selectedZonesIds,
        projectId: Number(projectId),
        dateFrom: format(new Date(formData.dateFrom), DATE_FORMAT_DATEPICKER_SHORT),
        dateTo: format(new Date(formData.dateTo), DATE_FORMAT_DATEPICKER_SHORT),
        recurringDays: isRecurringDaysSelected,
        timeFrom: Number(formData.timeOptions) === 0 ? '' : formData.startTime,
        timeTo: Number(formData.timeOptions) === 0 ? '' : formData.endTime,
        description: formData.comment
      };

      const response: string = await dispatch<any>(blockBookingSlot(data));
      if (response === 'BookingsExists') {
        handleBookingModal();
      } else if (response === 'OutOfWorkingHours') {
        setError('startTime', {
          type: 'manual',
          message: 'error'
        });

        dispatch(
          spawnGlobalNotification({
            delay: 2000,
            postition: NotificationPosition.Top,
            type: GlobalNotificationTypes.Error,
            message: t('projectSettings.blockBookingSlots.form.message.outOfWorkingHours')
          })
        );
      } else {
        dispatch(
          spawnGlobalNotification({
            postition: NotificationPosition.Top,
            type: GlobalNotificationTypes.Success,
            message: 'You have successfully created blocking slot'
          })
        );
        await dispatch(fetchBlockedSlots({ projectId }));
        onClose();
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const deliveryZoneOptions = useMemo(
    () => [
      {
        label: t('projectSettings.blockBookingSlots.form.option.lockZones'),
        value: 0
      },
      {
        label: t('projectSettings.blockBookingSlots.form.option.lockZone'),
        value: 1
      }
    ],
    [t]
  );

  const blockingTimeOptions = useMemo(
    () => [
      {
        label: t('projectSettings.blockBookingSlots.form.option.blockDay'),
        value: 0
      },
      {
        label: t('projectSettings.blockBookingSlots.form.option.blockTime'),
        value: 1
      }
    ],
    [t]
  );

  const title = <h3 className="modal-title">{t('projectSettings.blockBookingSlots.title')}</h3>;
  const buttons = (
    <div className="modal-buttons">
      <Button buttonTypes={['transparent']} color={Color.Blue} onClick={closeModalDialog}>
        {t('common.cancel')}
      </Button>
      <Button disabled={!isValid} buttonTypes={['blue']} type="submit" form="booking-filters">
        {t('common.block')}
      </Button>
    </div>
  );

  const startTime = watch('startTime');
  const endTime = watch('endTime');
  const timeOptions = watch('timeOptions');
  const isRecurring = watch('recurringOptions');

  const handleDeliveryZoneState = (isActive: boolean) => {
    deliveryZones?.forEach((item) => {
      setValue(`deliveryZone${item.order as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15}`, !!isActive);
    });
  };

  React.useEffect(() => {
    handleDeliveryZoneState(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!deliveryZones) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} closeModal={closeModalDialog} title={title} toolbar={buttons}>
      <form className="block-booking-slot-modal mt-n1" id="booking-filters" onSubmit={handleSubmit(onSubmit)}>
        <p className="form-label">{t('projectSettings.deliveryZones.title')}</p>
        <Controller
          name="allDeliveryZones"
          control={control}
          render={({ field: { onChange, name, value } }) => (
            <Radio
              id="role"
              name={name}
              options={deliveryZoneOptions}
              defaultValue={value}
              onChange={(e) => {
                onChange(e.target.value);
                handleDeliveryZoneState(Number(e.target.value) === 0);
              }}
            />
          )}
        />
        <div className="block-booking-slot-modal__checkboxes">
          {deliveryZones?.map((deliveryZone) => (
            <Controller
              key={deliveryZone.order}
              name={`deliveryZone${deliveryZone.order as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15}`}
              control={control}
              render={({ field: { onChange, value, name } }) => (
                <CheckBox
                  id={`delivery-zone-${deliveryZone.order}`}
                  name={name}
                  checked={value}
                  onChange={(e) => {
                    setValue('allDeliveryZones', 1);
                    onChange(e.target.checked);
                  }}
                  label={
                    <Badge
                      key={deliveryZone.order}
                      color={deliveryZoneColorMap[deliveryZone.order]}
                      icon={deliveryZone.order}
                      label={deliveryZone.name}
                    />
                  }
                />
              )}
            />
          ))}
        </div>
        <div className="d-flex">
          <div>
            <p className="form-label">{t('projectSettings.blockBookingSlots.form.option.from')}</p>
            <Controller
              name="dateFrom"
              control={control}
              render={({ field: { onChange, value, name } }) => (
                <Input
                  className="block-booking-slot-modal__date"
                  id="calendar-selected-date"
                  name={name}
                  type="date"
                  value={value}
                  error={errors.dateFrom?.message}
                  onChange={(e) => {
                    clearErrors();
                    onChange(e.target.value);
                  }}
                />
              )}
            />
          </div>
          <div className="block-booking-slot-modal__separator">-</div>
          <div>
            <p className="form-label">{t('projectSettings.blockBookingSlots.form.option.to')}</p>
            <Controller
              name="dateTo"
              control={control}
              render={({ field: { onChange, value, name } }) => (
                <Input
                  className="block-booking-slot-modal__date"
                  id="calendar-selected-date"
                  name={name}
                  type="date"
                  value={value}
                  error={errors.dateTo?.message}
                  onChange={(e) => {
                    clearErrors();
                    onChange(e.target.value);
                  }}
                />
              )}
            />
          </div>
        </div>
        <p className="form-label mt-3">{t('common.status')}</p>
        <div className="form-row">
          <Controller
            name="timeOptions"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <Radio id="timeOptions" name={name} options={blockingTimeOptions} defaultValue={value} onChange={onChange} />
            )}
          />

          <div className="range-time-picker__time-slot">
            <Controller
              name="startTime"
              control={control}
              render={({ fieldState: { error } }) => (
                <RangeTimePicker
                  label=""
                  id="delivery-time"
                  disabled={Number(timeOptions) === 0}
                  firstName="startTime"
                  secondName="endTime"
                  onChange={(range) => {
                    setValue('startTime', format(range.firstValue, TIME_FORMAT_BACKEND), { shouldDirty: true, shouldValidate: true });
                    setValue('endTime', format(range.secondValue, TIME_FORMAT_BACKEND), { shouldDirty: true, shouldValidate: true });
                  }}
                  firstValue={getTimeDate(startTime)}
                  secondValue={getTimeDate(endTime)}
                  disableBeforeValue={getTimeDate(operatingHoursFrom)}
                  disableAfterValue={getTimeDate(operatingHoursTo)}
                  required
                  error={error?.message}
                />
              )}
            />
          </div>
        </div>
        <Controller
          name="recurringOptions"
          control={control}
          render={({ field: { onChange, value, name } }) => (
            <CheckBox
              id="recurring-option"
              name={name}
              label={t('projectSettings.blockBookingSlots.form.option.recurring')}
              checked={value}
              onChange={onChange}
            />
          )}
        />
        {isRecurring && (
          <div className="block-booking-slot-modal__checkbox-days mt-3">
            {Array(7)
              .fill(0)
              .map((item, index) => (
                <Controller
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  name={`day${index as 0 | 1 | 2 | 3 | 4 | 5 | 6}`}
                  control={control}
                  render={({ field: { onChange, value, name } }) => (
                    <CheckBox
                      label={t(`projectSettings.blockBookingSlots.form.option.${index}`)}
                      id={`day-${index}`}
                      name={name}
                      checked={value}
                      onChange={onChange}
                    />
                  )}
                />
              ))}
          </div>
        )}
        <div className="mt-3">
          <Controller
            name="comment"
            control={control}
            render={({ field: { onChange, value, name } }) => (
              <TextArea
                label={t('common.comment')}
                id="comment"
                error={errors.comment?.message}
                name={name}
                value={value}
                onChange={(e) => onChange(e.target.value.replace(NEW_LINE_REGEX, ''))}
              />
            )}
          />
        </div>
      </form>
    </Modal>
  );
};
export default BlockBookingSlotModal;
